(() => {
  const MOBILE_MEDIA_QUERY = '(max-width: 767px)';

  class ProductGalleryNavigation extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.section = this.closest('[data-section-type="product-template"]');
      this.slider = this.section?.querySelector('[data-product-single-media-slider]');
      this.navigationStyle = this.dataset.navigationStyle || 'dots';
      this.mobilePeek = this.dataset.mobilePeek === 'true';
      this.mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
      this.dots = [...this.querySelectorAll('[data-product-gallery-dot]')];
      this.previousButton = this.querySelector('[data-product-gallery-previous]');
      this.nextButton = this.querySelector('[data-product-gallery-next]');
      this.currentCounter = this.querySelector('[data-product-gallery-current]');
      this.totalCounter = this.querySelector('[data-product-gallery-total]');
      this.flkty = null;
      this.bindFrame = null;
      this.bindAttempts = 0;

      if (!this.section || !this.slider || !window.theme?.Flickity) return;

      this.onFlickityChange = this.onFlickityChange.bind(this);
      this.onPreviousClick = this.onPreviousClick.bind(this);
      this.onNextClick = this.onNextClick.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onResize = this.onResize.bind(this);

      this.previousButton?.addEventListener('click', this.onPreviousClick);
      this.nextButton?.addEventListener('click', this.onNextClick);
      this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
      document.addEventListener('theme:resize:width', this.onResize);
      this.mobileMediaQuery.addEventListener?.('change', this.onResize);

      this.tryToBind();
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.bindFrame);
      this.detachFlickity();
      this.previousButton?.removeEventListener('click', this.onPreviousClick);
      this.nextButton?.removeEventListener('click', this.onNextClick);
      this.dots?.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
      document.removeEventListener('theme:resize:width', this.onResize);
      this.mobileMediaQuery?.removeEventListener?.('change', this.onResize);
    }

    tryToBind() {
      const instance = window.theme.Flickity.data(this.slider) || null;
      if (instance && instance.cells?.length > 1) {
        this.bindFlickity(instance);
        return;
      }

      if (this.bindAttempts < 120 && this.isConnected) {
        this.bindAttempts += 1;
        this.bindFrame = requestAnimationFrame(() => this.tryToBind());
      }
    }

    bindFlickity(instance) {
      if (this.flkty === instance) return;
      this.detachFlickity();
      this.flkty = instance;
      this.originalCellAlign = instance.options.cellAlign;
      this.flkty.on('change', this.onFlickityChange);
      this.applyMobilePeek();
      this.sync(this.flkty.selectedIndex || 0);
      this.hidden = this.navigationStyle === 'none';
      this.classList.add('is-ready');
    }

    detachFlickity() {
      if (this.flkty) {
        this.flkty.off?.('change', this.onFlickityChange);
      }
      this.flkty = null;
      this.classList.remove('is-ready');
      this.hidden = true;
    }

    onResize() {
      requestAnimationFrame(() => {
        const instance = window.theme.Flickity.data(this.slider) || null;
        if (instance !== this.flkty) {
          this.bindAttempts = 0;
          if (instance?.cells?.length > 1) {
            this.bindFlickity(instance);
          } else {
            this.detachFlickity();
            this.tryToBind();
          }
          return;
        }
        this.applyMobilePeek();
      });
    }

    applyMobilePeek() {
      if (!this.flkty) return;
      const peekActive = this.mobilePeek && this.mobileMediaQuery.matches;
      const targetAlign = peekActive ? 'left' : this.originalCellAlign;
      if (this.flkty.options.cellAlign !== targetAlign) {
        this.flkty.options.cellAlign = targetAlign;
      }
      requestAnimationFrame(() => this.flkty?.resize());
    }

    onFlickityChange(index) {
      this.sync(index);
    }

    onDotClick(event) {
      const index = Number(event.currentTarget.dataset.productGalleryDot);
      if (this.flkty && Number.isInteger(index)) this.flkty.select(index);
    }

    onPreviousClick() {
      this.flkty?.previous();
    }

    onNextClick() {
      this.flkty?.next();
    }

    sync(index) {
      if (!this.flkty) return;
      const slideCount = this.flkty.cells.length;
      this.dots.forEach((dot, dotIndex) => {
        dot.hidden = dotIndex >= slideCount;
        if (dotIndex === index) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (this.currentCounter) this.currentCounter.textContent = String(index + 1);
      if (this.totalCounter) this.totalCounter.textContent = String(slideCount);

      if (this.flkty.options.wrapAround === false) {
        if (this.previousButton) this.previousButton.disabled = index === 0;
        if (this.nextButton) this.nextButton.disabled = index === slideCount - 1;
      }
    }
  }

  class ProductStickyAddToCart extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.section = this.closest('[data-section-type="product-template"]');
      this.form = document.getElementById(this.dataset.formId);
      this.mainButton = this.form?.querySelector('[data-add-to-cart]');
      this.submitButton = this.querySelector('[data-sticky-atc-submit]');
      this.label = this.querySelector('[data-sticky-atc-label]');
      this.price = this.querySelector('[data-sticky-atc-price]');
      this.quantityInput = this.form?.querySelector('[name="quantity"]');
      this.quantityValue = this.querySelector('[data-sticky-atc-quantity-value]');
      this.minusButton = this.querySelector('[data-sticky-atc-minus]');
      this.plusButton = this.querySelector('[data-sticky-atc-plus]');
      this.deviceMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
      this.currentVariant = this.getCurrentVariant();
      this.bottomNav = null;
      this.bottomOffsetFrame = null;
      this.previousRootOffset = document.documentElement.style.getPropertyValue('--product-sticky-bottom-offset');
      this.previousRootHeight = document.documentElement.style.getPropertyValue('--product-sticky-atc-height');

      if (!this.section || !this.form || !this.mainButton || !this.submitButton) return;

      this.onVariantChange = this.onVariantChange.bind(this);
      this.onFormQuantityChange = this.onFormQuantityChange.bind(this);
      this.onMinusClick = this.onMinusClick.bind(this);
      this.onPlusClick = this.onPlusClick.bind(this);
      this.onSubmitClick = this.onSubmitClick.bind(this);
      this.onDeviceChange = this.onDeviceChange.bind(this);
      this.scheduleBottomOffset = this.scheduleBottomOffset.bind(this);

      this.section.addEventListener('theme:variant:change', this.onVariantChange);
      this.form.addEventListener('input', this.onFormQuantityChange);
      this.form.addEventListener('change', this.onFormQuantityChange);
      this.minusButton?.addEventListener('click', this.onMinusClick);
      this.plusButton?.addEventListener('click', this.onPlusClick);
      this.submitButton.addEventListener('click', this.onSubmitClick);
      this.deviceMediaQuery.addEventListener?.('change', this.onDeviceChange);
      window.addEventListener('resize', this.scheduleBottomOffset, {passive: true});

      this.stateObserver = new MutationObserver(() => this.syncFromMain());
      this.stateObserver.observe(this.mainButton, {attributes: true, childList: true, subtree: true});

      this.bottomNavMutationObserver = new MutationObserver(this.scheduleBottomOffset);
      this.bottomNavMutationObserver.observe(document.body, {childList: true, subtree: true});

      if ('ResizeObserver' in window) {
        this.barResizeObserver = new ResizeObserver(() => this.updateBarHeight());
        this.barResizeObserver.observe(this);
      }

      const initialize = () => {
        this.syncFromMain();
        this.syncQuantityFromMain();
        this.updateBottomOffset();
        this.setupVisibility();
        this.classList.add('is-ready');
        this.onDeviceChange();
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize, {once: true});
      } else {
        requestAnimationFrame(initialize);
      }
    }

    disconnectedCallback() {
      this.section?.removeEventListener('theme:variant:change', this.onVariantChange);
      this.form?.removeEventListener('input', this.onFormQuantityChange);
      this.form?.removeEventListener('change', this.onFormQuantityChange);
      this.minusButton?.removeEventListener('click', this.onMinusClick);
      this.plusButton?.removeEventListener('click', this.onPlusClick);
      this.submitButton?.removeEventListener('click', this.onSubmitClick);
      this.deviceMediaQuery?.removeEventListener?.('change', this.onDeviceChange);
      window.removeEventListener('resize', this.scheduleBottomOffset);
      this.visibilityObserver?.disconnect();
      this.stateObserver?.disconnect();
      this.bottomNavMutationObserver?.disconnect();
      this.bottomNavResizeObserver?.disconnect();
      this.barResizeObserver?.disconnect();
      cancelAnimationFrame(this.bottomOffsetFrame);
      document.body.classList.remove('has-product-sticky-atc');
      this.restoreRootProperty('--product-sticky-bottom-offset', this.previousRootOffset);
      this.restoreRootProperty('--product-sticky-atc-height', this.previousRootHeight);
    }

    restoreRootProperty(name, value) {
      if (value) {
        document.documentElement.style.setProperty(name, value);
      } else {
        document.documentElement.style.removeProperty(name);
      }
    }

    getCurrentVariant() {
      const productJsonElement = this.section?.querySelector('[data-product-json]');
      const variantId = Number(this.form?.querySelector('[name="id"]')?.value);
      if (!productJsonElement?.textContent || !variantId) return null;
      try {
        const productJson = JSON.parse(productJsonElement.textContent);
        return productJson.variants?.find((variant) => Number(variant.id) === variantId) || null;
      } catch (error) {
        return null;
      }
    }

    deviceAllowed() {
      const devices = this.dataset.devices || 'mobile';
      return devices === 'both' || (devices === 'mobile' && this.deviceMediaQuery.matches) || (devices === 'desktop' && !this.deviceMediaQuery.matches);
    }

    onDeviceChange() {
      const allowed = this.deviceAllowed();
      document.body.classList.toggle('has-product-sticky-atc', allowed);
      if (!allowed) {
        this.setVisible(false);
        return;
      }
      this.updateBottomOffset();
      this.updateVisibility();
    }

    setupVisibility() {
      if (this.dataset.trigger === 'always') {
        this.updateVisibility();
        return;
      }

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          this.mainButtonEntry = entries[0];
          this.updateVisibility();
        });
        this.visibilityObserver.observe(this.mainButton);
      } else {
        this.mainButtonEntry = {isIntersecting: false, boundingClientRect: {top: -1}};
        this.updateVisibility();
      }
    }

    updateVisibility() {
      if (!this.deviceAllowed() || !this.classList.contains('is-ready')) {
        this.setVisible(false);
        return;
      }

      if (this.dataset.trigger === 'always') {
        this.setVisible(true);
        return;
      }

      if (!this.mainButtonEntry) {
        this.setVisible(false);
        return;
      }

      const hasPassedButton = this.mainButtonEntry.boundingClientRect.top < 0;
      this.setVisible(!this.mainButtonEntry.isIntersecting && hasPassedButton);
    }

    setVisible(visible) {
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', String(!visible));
    }

    onVariantChange(event) {
      this.currentVariant = event.detail?.variant || null;
      this.updateQuantityRules();
      requestAnimationFrame(() => {
        this.syncFromMain();
        this.syncQuantityFromMain();
      });
    }

    syncFromMain() {
      if (!this.mainButton || !this.submitButton) return;
      const mainText = this.mainButton.querySelector('[data-add-to-cart-text]')?.cloneNode(true);
      mainText?.querySelectorAll('[data-product-price], [data-compare-price]').forEach((element) => element.remove());
      const translatedLabel = mainText?.textContent.replace(/\s+/g, ' ').trim();
      if (translatedLabel && this.label) this.label.textContent = translatedLabel;

      const variantAvailable = this.currentVariant ? this.currentVariant.available : !this.mainButton.disabled;
      this.submitButton.disabled = this.mainButton.disabled || !variantAvailable;

      if (this.price) {
        const productPrice = [...this.section.querySelectorAll('[data-price-wrapper]:not([data-atc-button]) [data-product-price]')].find((element) => !this.contains(element));
        this.price.textContent = productPrice?.textContent.trim() || '';
        this.price.hidden = this.price.textContent === '';
      }
    }

    onSubmitClick() {
      if (this.submitButton.disabled || this.mainButton.disabled) return;
      this.submitButton.disabled = true;
      this.mainButton.click();
    }

    onFormQuantityChange(event) {
      if (event.target.name !== 'quantity') return;
      this.quantityInput = event.target;
      this.syncQuantityFromMain();
    }

    quantityRules() {
      const rule = this.currentVariant?.quantity_rule || {};
      const min = Number(rule.min || this.quantityInput?.min || 1);
      const maxValue = rule.max ?? this.quantityInput?.max;
      const max = maxValue === '' || maxValue == null ? null : Number(maxValue);
      const step = Number(rule.increment || this.quantityInput?.step || 1);
      return {min, max: Number.isFinite(max) ? max : null, step};
    }

    normalizeQuantity(value) {
      const {min, max, step} = this.quantityRules();
      let normalized = Number(value);
      if (!Number.isFinite(normalized)) normalized = min;
      normalized = min + Math.round((normalized - min) / step) * step;
      normalized = Math.max(min, normalized);
      if (max !== null) normalized = Math.min(max, normalized);
      return normalized;
    }

    updateQuantityRules() {
      if (!this.quantityInput) return;
      const {min, max, step} = this.quantityRules();
      this.quantityInput.min = String(min);
      this.quantityInput.step = String(step);
      if (max === null) {
        this.quantityInput.removeAttribute('max');
      } else {
        this.quantityInput.max = String(max);
      }
    }

    syncQuantityFromMain() {
      if (!this.quantityValue || !this.quantityInput) return;
      const quantity = this.normalizeQuantity(this.quantityInput.value);
      this.quantityValue.value = String(quantity);
      const {min, max} = this.quantityRules();
      if (this.minusButton) this.minusButton.disabled = quantity <= min;
      if (this.plusButton) this.plusButton.disabled = max !== null && quantity >= max;
    }

    setQuantity(value) {
      if (!this.quantityInput) return;
      const quantity = this.normalizeQuantity(value);
      this.quantityInput.value = String(quantity);
      this.syncQuantityFromMain();
      this.quantityInput.dispatchEvent(new Event('change', {bubbles: true}));
    }

    onMinusClick() {
      const {step} = this.quantityRules();
      this.setQuantity(Number(this.quantityInput?.value || 0) - step);
    }

    onPlusClick() {
      const {step} = this.quantityRules();
      this.setQuantity(Number(this.quantityInput?.value || 0) + step);
    }

    scheduleBottomOffset() {
      if (this.bottomOffsetFrame) return;
      this.bottomOffsetFrame = requestAnimationFrame(() => {
        this.bottomOffsetFrame = null;
        this.updateBottomOffset();
      });
    }

    updateBarHeight() {
      if (this.offsetHeight > 0) {
        document.documentElement.style.setProperty('--product-sticky-atc-height', `${this.offsetHeight}px`);
      }
    }

    updateBottomOffset() {
      if (!this.deviceAllowed()) return;
      const declaredHeight =
        parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--mobile-bottom-nav-height')) ||
        parseFloat(getComputedStyle(document.body).getPropertyValue('--mobile-bottom-nav-height')) ||
        0;
      let height = declaredHeight;
      let detectedNav = null;

      if (height === 0 && this.deviceMediaQuery.matches) {
        const candidates = document.querySelectorAll(
          '[data-mobile-bottom-navigation], [data-bottom-navigation], nav, [role="navigation"], [class*="bottom-nav"], [class*="bottom_nav"], [id*="bottom-nav"], [id*="bottom_nav"]'
        );
        detectedNav = [...candidates].find((element) => {
          if (this.contains(element) || element.closest('.drawer, .popup, cart-success-toast')) return false;
          const styles = getComputedStyle(element);
          const rect = element.getBoundingClientRect();
          return (
            (styles.position === 'fixed' || styles.position === 'sticky') &&
            styles.display !== 'none' &&
            styles.visibility !== 'hidden' &&
            rect.width >= window.innerWidth * 0.6 &&
            rect.height >= 32 &&
            rect.height <= 140 &&
            rect.top > window.innerHeight / 2 &&
            Math.abs(window.innerHeight - rect.bottom) <= 8
          );
        });

        if (detectedNav) {
          const rect = detectedNav.getBoundingClientRect();
          height = Math.max(0, window.innerHeight - rect.top);
        }
      }

      document.documentElement.style.setProperty('--product-sticky-bottom-offset', height > 0 ? `${Math.round(height)}px` : 'env(safe-area-inset-bottom, 0px)');
      this.updateBarHeight();

      if (detectedNav !== this.bottomNav && 'ResizeObserver' in window) {
        this.bottomNavResizeObserver?.disconnect();
        this.bottomNav = detectedNav;
        if (detectedNav) {
          this.bottomNavResizeObserver = new ResizeObserver(this.scheduleBottomOffset);
          this.bottomNavResizeObserver.observe(detectedNav);
        }
      }
    }
  }

  if (!customElements.get('product-gallery-navigation')) {
    customElements.define('product-gallery-navigation', ProductGalleryNavigation);
  }

  if (!customElements.get('product-sticky-add-to-cart')) {
    customElements.define('product-sticky-add-to-cart', ProductStickyAddToCart);
  }
})();
